import React, { useState, useEffect } from 'react';
import { Colors, Metrics, Images, StyledComponents } from '../../themes';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import SvgIcon from '../../icons/SvgIcon';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import Table from '../common/Table';
import Select from '../common/Select';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// include all the lines below for date picker
import DatePicker, { registerLocale } from 'react-datepicker';
import i18n from '../../i18n';
import '../common/styles/react-datepicker.custom.css';
import fi from 'date-fns/locale/fi';
import pl from 'date-fns/locale/pl';
import de from 'date-fns/locale/de';
import sv from 'date-fns/locale/sv';
import es from 'date-fns/locale/es';
registerLocale('fi', fi);
registerLocale('pl', pl);
registerLocale('de', de);
registerLocale('sv', sv);
registerLocale('es', es);
// end of date picker

const { StyledTextField } = StyledComponents;

const useStyles = makeStyles((theme) => {
  const time = i18n.t('common.time');
  return {
    editTagContainer: {
      paddingLeft: 10,
      display: 'flex',
      flexDirection: 'column',
      overflowWrap: 'break-word',
      width: 510,
      [theme.breakpoints.down(620)]: {
        paddingLeft: 0,
        width: 400,
      },
      [theme.breakpoints.down(520)]: {
        paddingLeft: 0,
        width: 350,
      },
      [theme.breakpoints.down(430)]: {
        paddingLeft: 0,
        width: 300,
      },
      minHeight: 400,
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    closeButton: {
      marginRight: -12,
    },
    informationSection: {
      marginTop: theme.spacing(4),
    },
    accessLevelsSection: {
      marginTop: theme.spacing(4),
    },
    formFieldsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-around',
      '& .MuiInputBase-input': {
        color: Colors.black,
      },
      '& .react-datepicker-time__header': {
        visibility: 'hidden',
        '&::before': {
          content: `'  ${time}'`,
          visibility: 'visible',
          paddingLeft: 14,
        },
      },
    },
    textField: {
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(4),
      width: 220,
      [theme.breakpoints.down(580)]: {
        width: 250,
      },
      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
        borderBottom: `2px solid ${Colors.hoverGrey}`,
      },
    },
    footerButtonsContainer: {
      minHeight: 80,
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      borderRadius: '4px 4px 0 0',
      padding: theme.spacing(4),
      [theme.breakpoints.down(Metrics.minWidth)]: {
        padding: theme.spacing(2),
        justifyContent: 'flex-start',
      },
      [theme.breakpoints.between('sm', 'lg')]: {
        padding: theme.spacing(2),
        minHeight: 60,
      },
    },
    button: {
      fontSize: 14,
      color: Colors.black,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
        color: Colors.white,
      },
    },
    addAccessLevelsButton: {
      marginRight: 'auto',
      marginTop: 20,
    },
    errorButton: {
      color: Colors.errorRed,
      borderColor: Colors.errorRed,
      '&:hover': {
        borderColor: Colors.disabledGrey,
      },
    },
    errorText: {
      color: Colors.errorRed,
      fontSize: '0.75rem',
      marginTop: 4,
    },
    saveButton: {
      marginLeft: theme.spacing(1),
      fontSize: 14,
      color: Colors.white,
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    datePicker: {
      marginTop: 16,
    },
    informationTitleContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingRight: 20,
    },
    switchAndPhoneIconContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    emailValidationError: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      color: Colors.errorRed,
      fontSize: '0.75rem',
      marginTop: 4,
      marginRight: 20,
    },
  };
});

const TagEditModal = (props) => {
  const isOneColumn = useMediaQuery(`(max-width:${Metrics.oneColumn}px)`);
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    onClose,
    openAccessLevelsModal,
    onClickSave,
    onChange,
    selectedStartDate,
    selectedEndDate,
    tagUid,
    accesslevels,
    tagTypeId,
    tagPin,
    isHIDSupported,
    useHID,
    onChangeUseHID,
    showHIDEmailValidationError,
    mobileId,
    isNewTag,
  } = props;
  const handleSubmit = (event) => {
    event.preventDefault();
    onClickSave();
  };

  // tagUid
  const [isTagUidError, setTagUidError] = useState(false);
  const [tagUidHelperText, setTagUidHelperText] = useState(null);
  const [isTagUidTouched, setTagUidTouched] = useState(false);
  // accessLevels
  const [isAccessLevelsError, setAccessLevelsError] = useState(false);
  const [isAccessLevelsTouched, setAccessLevelsTouched] = useState(false);
  // tagType
  const [isTagTypeError, setTagTypeError] = useState(false);
  const [tagTypeHelperText, setTagTypeHelperText] = useState(null);
  const [isTagTypeTouched, setTagTypeTouched] = useState(false);
  // tagPin
  const [isTagPinError, setTagPinError] = useState(null);
  const [tagPinHelperText, setTagPinHelperText] = useState(null);
  const [isTagPinTouched, setTagPinTouched] = useState(false);

  const [isFormValid, setFormValid] = useState(false);

  useEffect(() => {
    const validate = () => {
      const isTagUidNotEmpty = tagUid !== '';
      const isTagUidLongEnough = tagUid.length > 1;
      const isAccessLevelsValid = accesslevels && accesslevels.length > 0;
      const isTagTypeValid = tagTypeId !== '';
      const isTagPinLongEnough = tagPin.length === 0 || tagPin.length > 1;
      const isTagPinDigits = /^\d+$/.test(tagPin) || tagPin === '';

      if (
        (!isTagUidTouched && (!isTagUidNotEmpty || !isTagUidLongEnough)) ||
        (!isAccessLevelsTouched && !isAccessLevelsValid) ||
        (!isTagTypeTouched && !isTagTypeValid) ||
        (!isTagPinTouched && (!isTagPinLongEnough || !isTagPinDigits))
      ) {
        setFormValid(false);
      }

      if (isTagUidTouched) {
        if (!isTagUidNotEmpty) {
          setTagUidError(true);
          setTagUidHelperText(t('validation.requiredField'));
          setFormValid(false);
        } else if (isTagUidNotEmpty) {
          setTagUidError(false);
          setTagUidHelperText(null);
          if (!isTagUidLongEnough) {
            setTagUidError(true);
            setTagUidHelperText(t('validation.atLeastTwoCharacters'));
            setFormValid(false);
          } else if (isTagUidLongEnough) {
            setTagUidError(false);
            setTagUidHelperText(null);
          }
        }
      }
      if (isHIDSupported && useHID) {
        setTagUidError(false);
        setTagUidHelperText(t('persons.willBeAutoGenerated'));
      }

      if (isTagPinTouched) {
        if (!isTagPinLongEnough) {
          setTagPinError(true);
          setTagPinHelperText(t('validation.atLeastTwoDigits'));
          setFormValid(false);
        } else if (isTagPinLongEnough) {
          setTagPinError(false);
          setTagPinHelperText(null);
          if (!isTagPinDigits) {
            setTagPinError(true);
            setTagPinHelperText(t('validation.containsOnlyDigits'));
            setFormValid(false);
          } else if (isTagPinDigits) {
            setTagPinError(false);
            setTagPinHelperText(null);
          }
        }
      }

      if (isAccessLevelsTouched) {
        if (!isAccessLevelsValid) {
          setAccessLevelsError(true);
          setFormValid(false);
        } else if (isAccessLevelsValid) {
          setAccessLevelsError(false);
        }
      }

      if (isTagTypeTouched) {
        if (!isTagTypeValid) {
          setTagTypeError(true);
          setTagTypeHelperText(t('validation.requiredField'));
          setFormValid(false);
        } else if (isTagTypeValid) {
          setTagTypeError(false);
          setTagTypeHelperText(null);
        }
      }

      if (
        ((isTagUidNotEmpty && isTagUidLongEnough) || (isHIDSupported && useHID)) &&
        isAccessLevelsValid &&
        isTagTypeValid &&
        isTagPinLongEnough &&
        isTagPinDigits
      ) {
        setFormValid(true);
      }
    };
    validate();
  }, [
    tagUid,
    accesslevels,
    tagTypeId,
    tagPin,
    isTagUidTouched,
    isAccessLevelsTouched,
    isTagTypeTouched,
    isTagPinTouched,
    isHIDSupported,
    useHID,
    t,
  ]);

  const handleBlur = (event) => {
    const { name, localName } = event.target;
    // one possibility is to show all errors after first touch.
    // it has the advantage of guiding the user in what is missing:
    setTagUidTouched(true);
    setTagPinTouched(true);
    setTagTypeTouched(true);
    setAccessLevelsTouched(true);
    // the other possibility is to show errors after each field has been touched.
    // Delete the 4 lines above and uncomment following lines:

    // if (name === 'tagUid') {
    //   setTagUidTouched(true);
    // }
    // if (name === 'tagPin') {
    //   setTagPinTouched(true);
    // }
    // if (localName === 'div') {
    //   // tagType touched
    //   setTagTypeTouched(true);
    // }
    // if (name === 'accessLevels') {
    //   setAccessLevelsTouched(true);
    // }
  };

  return (
    <div className={classes.editTagContainer}>
      <form
        id="personForm"
        className={classes.formContainer}
        noValidate
        onSubmit={handleSubmit}
        autoComplete="off"
        // onKeyPress={(event) => onKeyPress(event)}
      >
        <div className={classes.header}>
          <Typography variant="h2" id="modal-title">
            {props.title}
          </Typography>
          <IconButton className={classes.closeButton} key="close" aria-label="close" color="inherit" onClick={onClose}>
            <CloseIcon className={classes.icon} />
          </IconButton>
        </div>
        <div className={classes.informationSection}>
          <div className={classes.informationTitleContainer}>
            <Typography variant="h2">{t('common.information')}</Typography>
            {isHIDSupported && (isNewTag || mobileId) && (
              <div className={classes.switchAndPhoneIconContainer}>
                <img className={classes.phoneIcon} src={isNewTag ? Images.MobilePhoneDarkGrey : Images.MobilePhoneDisabledGrey} />
                <FormControlLabel
                  className={classes.switch}
                  control={
                    <Switch
                      checked={(useHID && isNewTag) || (!isNewTag && mobileId)}
                      onChange={(event) => onChangeUseHID(event)}
                      value="useHID"
                    />
                  }
                  label={t(isNewTag ? 'persons.getMobileId' : 'persons.isMobileId')}
                  labelPlacement="start"
                  disabled={!isNewTag}
                />
              </div>
            )}
          </div>
          {isHIDSupported && showHIDEmailValidationError && isNewTag && (
            <div className={classes.emailValidationError}>
              <span>{t('persons.noValidEmail')}</span>
            </div>
          )}
          <div className={classes.formFieldsContainer}>
            {/* next 2 lines help to deactivate form autocompletion in Chrome */}
            <input type="email" style={{ opacity: 0, position: 'absolute' }} autoComplete="off" />
            <input type="password" style={{ opacity: 0, position: 'absolute' }} autoComplete="new-password" />
            {/* tagUid */}
            <StyledTextField
              id="tagUid"
              className={classes.textField}
              label={t('common.uid')}
              name="tagUid"
              margin="normal"
              value={isHIDSupported && useHID ? '' : tagUid}
              onChange={onChange}
              required={!isHIDSupported && !useHID}
              onBlur={handleBlur}
              error={isTagUidError && !(isHIDSupported && useHID)}
              helperText={tagUidHelperText}
              disabled={!props.isNewTag || (isHIDSupported && useHID)}
            />
            {/* person */}
            <StyledTextField
              id="person"
              className={classes.textField}
              label={t('persons.person')}
              name="person"
              margin="normal"
              disabled
              value={props.person}
              onChange={onChange}
            />
            {/* select Status */}
            <Select
              selectData={props.statusSelectData}
              selectLabel={props.statusSelectLabel}
              selectValue={props.statusSelectValue}
              onSelect={props.statusOnSelect}
              accessValue={props.statusAccessValue}
              accessLabel={props.statusAccessLabel}
              noEntry={props.noEntry}
              selectWidth={props.selectWidth}
              lightTheme={props.lightTheme}
              formStyle={props.formStyle}
              disabled={props.disabled || props.isNewTag}
            />
            {/* tagPin */}
            <StyledTextField
              id="tagPin"
              type="tel"
              pattern="[0-9]"
              className={classes.textField}
              label={t('persons.pinCode')}
              name="tagPin"
              margin="normal"
              value={tagPin}
              onChange={onChange}
              onBlur={handleBlur}
              // autoComplete="none"
              error={isTagPinError}
              helperText={tagPinHelperText}
            />
            {/* select tagType */}
            <Select
              selectData={props.tagTypesSelectData}
              selectLabel={props.tagTypesSelectLabel}
              selectValue={props.tagTypesSelectValue}
              onSelect={props.tagTypesOnSelect}
              accessValue={props.tagTypesAccessValue}
              accessLabel={props.tagTypesAccessLabel}
              noEntry={props.noEntry}
              selectWidth={props.selectWidth}
              lightTheme={props.lightTheme}
              formStyle={props.formStyle}
              required
              disabled={props.disabled}
              error={isTagTypeError}
              helperText={tagTypeHelperText}
              name="tagType"
            />
            {/* accessLeft */}
            <StyledTextField
              id="accessLeft"
              className={classes.textField}
              label={t('persons.accessCount')}
              name="accessLeft"
              margin="normal"
              value={props.accessLeft}
              onChange={onChange}
            />
            {/* startDate */}
            <DatePicker
              className={classes.datePicker}
              showPopperArrow={false}
              selected={selectedStartDate ? new Date(selectedStartDate) : new Date()}
              onChange={props.handleStartDateChange}
              locale={i18n.language}
              dateFormat="d.M.yyyy H:mm"
              timeFormat="H:mm"
              timeIntervals={1}
              shouldCloseOnSelect={false}
              timeInputLabel={`${t('common.time')}:`}
              showTimeInput={true}
              {...(isOneColumn && {
                popperModifiers: {
                  offset: {
                    enabled: true,
                    offset: '15px, 10px',
                  },
                  preventOverflow: {
                    enabled: true,
                    escapeWithReference: false,
                    boundariesElement: 'viewport',
                  },
                },
              })}
              customInput={<StyledTextField className={classes.textField} label={t('persons.from')} />}
            />
            {/* endDate */}
            <DatePicker
              className={classes.datePicker}
              showPopperArrow={false}
              selected={selectedEndDate ? new Date(selectedEndDate) : null}
              {...(selectedStartDate && { minDate: new Date(selectedStartDate) })}
              onChange={props.handleEndDateChange}
              locale={i18n.language}
              dateFormat="d.M.yyyy H:mm"
              timeFormat="H:mm"
              timeIntervals={1}
              shouldCloseOnSelect={false}
              timeInputLabel={`${t('common.time')}:`}
              showTimeInput={true}
              popperModifiers={{
                offset: {
                  enabled: true,
                  offset: isOneColumn ? '15px, 10px' : '-20px, 10px',
                },
                preventOverflow: {
                  enabled: true,
                  escapeWithReference: false,
                  boundariesElement: 'viewport',
                },
              }}
              customInput={<StyledTextField className={classes.textField} label={t('persons.to')} />}
            />
          </div>
        </div>
        <div className={classes.accessLevelsSection}>
          <Typography variant="h2">{t('navigation.accessLevels')}</Typography>
          <Button
            type="button"
            name="accessLevels"
            onBlur={handleBlur}
            variant="outlined"
            className={clsx(classes.button, classes.addAccessLevelsButton, isAccessLevelsError && classes.errorButton)}
            onClick={openAccessLevelsModal}
            endIcon={<AddIcon width={20} />}
          >
            {t('accessLevels.addAccessLevels')}
            {'* '}
          </Button>
          {isAccessLevelsError && (
            <Typography className={classes.errorText}>
              {t('personManager_AddTag_noAccessLevelSelectedError')}
            </Typography>
          )}
          {props.accessLevelsHasDuplicates && !props.accessLevelsHasDuplicatesisLoading && (
            <Typography className={classes.errorText}>{t('personManager_AddTag_hasDuplicateControllers')}</Typography>
          )}
          <Table {...props} />
        </div>
        <div className={classes.footerButtonsContainer}>
          <Button type="button" variant="outlined" className={classes.button} onClick={onClose}>
            {t('Global_cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={classes.saveButton}
            // onClick={onClickSave}
            endIcon={<SvgIcon icon="Save" width={20} color={!isFormValid ? Colors.disabledGrey : Colors.white} />}
            disabled={
              !isFormValid ||
              props.accessLevelsHasDuplicates ||
              props.accessLevelsHasDuplicatesisLoading ||
              showHIDEmailValidationError
            }
          >
            {t('Global_save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TagEditModal;
